import { useCasesStore } from '~/stores/cases'
import { useClientsStore } from '~/stores/clients'
import { useDocumentsStore } from '~/stores/documents'

/**
 * Link one document into a case or client, as a MOVE — the one write every reassignment surface
 * performs (the drag-and-drop drop target in `BrowserGroupedList.vue`, and the "Move to"
 * context-menu submenu in `FileExplorer.vue`, AlexAmin/marcus#846/#847), extracted so neither can
 * drift from the other.
 *
 * **A move, not an accumulating link (AlexAmin/marcus#848).** The first version of this only ever
 * added the destination link, which was correct for the one case it was built against — Unassigned
 * has nothing to remove — and silently wrong for reassigning between two NAMED cases/clients: a
 * document dragged from case A onto case B kept its `case_documents` row for A, so it read as
 * copied rather than moved. Confirmed against the dev DB: a test document dragged through four
 * cases ended up linked to all four. So every OTHER case (or client) already holding this document
 * is unlinked once the new link is in place, per axis — a case move never touches a client-direct
 * link and vice versa, since those are different questions ("which matter" vs "which client's
 * general shelf").
 *
 * Add runs BEFORE remove: if the remove calls fail partway, the document is left in more than one
 * place (recoverable, and the same failure shape the original bug had) rather than orphaned in
 * none.
 *
 * `addCaseDocument` mirrors its own membership locally (`cases.ts`'s `link`/`unlink`);
 * `addClientDocument`/`removeClientDocument` do not, so the client half also patches the documents
 * store's `client_documents` mirror here — see `BrowserGroupedList.vue`'s history for why the
 * client half needed that second write.
 */
export function useLinkDocumentToGroup() {
  const documents = useDocumentsStore()
  const cases = useCasesStore()
  const clients = useClientsStore()

  function otherCasesHolding(itemId: string, exceptCaseId: string): string[] {
    const ids: string[] = []
    for (const c of cases.byId.values()) {
      if (c.id !== exceptCaseId && c.documentIds.includes(itemId)) ids.push(c.id)
    }
    return ids
  }

  function otherClientsHolding(itemId: string, exceptClientId: string): string[] {
    const ids: string[] = []
    for (const [clientId, itemIds] of documents.clientDirectIds) {
      if (clientId !== exceptClientId && itemIds.includes(itemId)) ids.push(clientId)
    }
    return ids
  }

  async function linkDocumentToGroup(kind: 'case' | 'project', groupId: string, itemId: string): Promise<void> {
    if (kind === 'project') {
      const previousClients = otherClientsHolding(itemId, groupId)
      await clients.addClientDocument(groupId, itemId)
      documents.linkToClient(groupId, itemId)
      for (const clientId of previousClients) {
        await clients.removeClientDocument(clientId, itemId)
        documents.unlinkFromClient(clientId, itemId)
      }
      return
    }
    const previousCases = otherCasesHolding(itemId, groupId)
    await cases.addCaseDocument(groupId, itemId, '/')
    for (const caseId of previousCases) {
      await cases.removeCaseDocument(caseId, itemId)
    }
  }

  return { linkDocumentToGroup }
}
